(function () {
  'use strict';
  const ttl = 15 * 60 * 1000;
  const failureCooldown = 60 * 1000;
  const productLimit = 25000;
  const wordLimit = 50000;
  const metadata = document.currentScript?.dataset || {};
  const country = /^[A-Z]{2}$/.test(metadata.country || '') ? metadata.country : null;
  function languageCode(value) {
    const normalized = String(value || '').toUpperCase().replace(/-/g, '_');
    if (/^ZH(?:_|$)/.test(normalized)) return /TW|HANT/.test(normalized) ? 'ZH_TW' : 'ZH_CN';
    if (/^PT(?:_|$)/.test(normalized)) return normalized === 'PT_BR' ? 'PT_BR' : 'PT_PT';
    const primary = normalized.split('_')[0];
    return /^[A-Z]{2}$/.test(primary) || primary === 'FIL' || primary === 'CKB' ? primary : null;
  }
  const language = languageCode(metadata.language);
  const storageKey = `wokiee-search-lexicon-v1:${country || ''}:${language || ''}`;
  const query = `query SearchLexicon($after: String, $country: CountryCode, $language: LanguageCode) @inContext(country: $country, language: $language) {
    products(first: 250, after: $after, sortKey: ID) {
      pageInfo { hasNextPage endCursor }
      nodes { id title description productType vendor onlineStoreUrl }
    }
  }`;
  let words = null;
  let loadedAt = 0;
  let revision = 0;
  let checkedStorage = false;
  let pending = null;
  let active = null;
  let generation = 0;
  let retryAfter = 0;

  function validWord(value) {
    if (typeof value !== 'string' || value !== value.normalize('NFC').toLowerCase()) return false;
    const letters = Array.from(value);
    return letters.length >= 5 && letters.length <= 48 && /^\p{L}+$/u.test(value) &&
      letters.every(letter => letter.toLowerCase() !== letter.toUpperCase());
  }

  function peek() {
    if (words && Date.now() - loadedAt >= 0 && Date.now() - loadedAt < ttl) return words;
    if (words) { words = null; loadedAt = 0; revision++; }
    if (!checkedStorage) {
      checkedStorage = true;
      try {
        const raw = window.sessionStorage.getItem(storageKey);
        if (!raw || raw.length > 2000000) return null;
        const cached = JSON.parse(raw);
        const age = Date.now() - cached.time;
        if (cached.version !== 1 || typeof cached.time !== 'number' || !Number.isFinite(age) || age < 0 || age >= ttl ||
          !Array.isArray(cached.words) || cached.words.length > wordLimit || !cached.words.every(validWord) ||
          new Set(cached.words).size !== cached.words.length) return null;
        words = Object.freeze(cached.words); loadedAt = cached.time; revision++;
        return words;
      } catch (error) { /* Storage is optional in private/restricted browsers. */ }
    }
    return null;
  }

  function abortError() { const error = new Error('Word lookup cancelled.'); error.name = 'AbortError'; return error; }
  function checkAbort(signal) { if (signal.aborted) throw abortError(); }

  function yieldToPage(signal) {
    checkAbort(signal);
    return new Promise((resolve, reject) => {
      const onAbort = () => { window.clearTimeout(timer); reject(abortError()); };
      const timer = window.setTimeout(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      }, 0);
      signal.addEventListener('abort', onAbort, { once: true });
      if (signal.aborted) onAbort();
    }).then(() => checkAbort(signal));
  }

  async function request(after, signal) {
    for (let attempt = 0; attempt < 2; attempt++) {
      checkAbort(signal);
      const controller = new AbortController();
      let timer, onAbort;
      const deadline = new Promise((resolve, reject) => {
        onAbort = () => { controller.abort(); reject(abortError()); };
        signal.addEventListener('abort', onAbort, { once: true });
        timer = window.setTimeout(() => { controller.abort(); reject(new Error('Word lookup timed out.')); }, 10000);
      });
      let result;
      try {
        result = await Promise.race([deadline, (async () => {
          const response = await fetch('/api/2026-07/graphql.json', {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ query, variables: { after, country, language } }),
            signal: controller.signal, cache: 'no-store',
          });
          const text = await response.text();
          checkAbort(signal);
          return { ok: response.ok, status: response.status, text };
        })()]);
      } finally {
        window.clearTimeout(timer);
        signal.removeEventListener('abort', onAbort);
      }
      if ((result.status === 429 || result.status === 503) && attempt === 0) {
        await new Promise((resolve, reject) => {
          const onStop = () => { window.clearTimeout(retryTimer); reject(abortError()); };
          const retryTimer = window.setTimeout(() => { signal.removeEventListener('abort', onStop); resolve(); }, 400);
          signal.addEventListener('abort', onStop, { once: true });
          if (signal.aborted) onStop();
        });
        continue;
      }
      if (!result.ok) throw new Error('Word lookup unavailable.');
      const payload = JSON.parse(result.text);
      if (payload.errors?.length || !payload.data?.products) throw new Error('Incomplete word lookup.');
      return payload.data.products;
    }
  }

  async function scan(signal) {
    const frequencies = new Map();
    const productIds = new Set();
    const cursors = new Set();
    let after = null;
    const collect = (text, weight) => {
      const values = new Set(String(text).normalize('NFC').toLowerCase().match(/[\p{L}\p{M}\p{N}]+/gu) || []);
      for (const word of values) {
        if (!validWord(word)) continue;
        frequencies.set(word, (frequencies.get(word) || 0) + weight);
        if (frequencies.size > wordLimit) throw new Error('Word lookup too large.');
      }
    };
    do {
      checkAbort(signal);
      const page = await request(after, signal);
      if (!Array.isArray(page.nodes) || page.nodes.length > 250 || typeof page.pageInfo?.hasNextPage !== 'boolean') {
        throw new Error('Incomplete product page.');
      }
      for (const product of page.nodes) {
        checkAbort(signal);
        if (typeof product?.id !== 'string' || !product.id || productIds.has(product.id) ||
          !['title', 'description', 'productType', 'vendor'].every(field => typeof product[field] === 'string')) {
          throw new Error('Incomplete product data.');
        }
        productIds.add(product.id);
        if (productIds.size > productLimit) throw new Error('Product lookup too large.');
        if (product.onlineStoreUrl) {
          collect(product.title, 8); collect(product.productType, 4); collect(product.vendor, 4); collect(product.description, 1);
        }
        // Long descriptions are processed in small batches so typing and
        // rendering can run while the complete catalog dictionary is built.
        if (productIds.size % 25 === 0) await yieldToPage(signal);
      }
      after = null;
      if (page.pageInfo.hasNextPage) {
        const cursor = page.pageInfo.endCursor;
        if (!page.nodes.length || typeof cursor !== 'string' || !cursor || cursors.has(cursor) || productIds.size >= productLimit) {
          throw new Error('Incomplete product pagination.');
        }
        cursors.add(cursor); after = cursor;
      }
    } while (after);
    checkAbort(signal);
    return [...frequencies].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([word]) => word);
  }

  function load() {
    const cached = peek();
    if (cached) return Promise.resolve(cached);
    if (pending) return pending;
    // An unavailable API must not restart a full scan on every keystroke.
    if (Date.now() < retryAfter) return Promise.resolve([]);
    const current = ++generation;
    const controller = new AbortController(); active = controller;
    let timer;
    const deadline = new Promise((resolve, reject) => {
      timer = window.setTimeout(() => { controller.abort(); reject(new Error('Catalog word lookup timed out.')); }, 15000);
    });
    const task = (async () => {
      try {
        const result = await Promise.race([scan(controller.signal), deadline]);
        if (current !== generation || controller.signal.aborted) return [];
        words = Object.freeze(result); loadedAt = Date.now(); revision++; retryAfter = 0;
        try { window.sessionStorage.setItem(storageKey, JSON.stringify({ version: 1, time: loadedAt, words })); }
        catch (error) { /* A full or disabled cache does not prevent searching. */ }
        return words;
      } catch (error) {
        controller.abort();
        if (current === generation) retryAfter = Date.now() + failureCooldown;
        return [];
      } finally {
        window.clearTimeout(timer);
        if (current === generation) { pending = null; active = null; }
      }
    })();
    pending = task;
    return task;
  }

  function stop() { generation++; active?.abort(); active = null; pending = null; retryAfter = 0; }
  function refresh() {
    stop(); words = null; loadedAt = 0; revision++; checkedStorage = true;
    try { window.sessionStorage.removeItem(storageKey); } catch (error) { /* Optional cache. */ }
    return load();
  }
  window.addEventListener('pagehide', stop);
  window.WokieeSearchLexicon = Object.freeze({ load, peek, revision: () => revision, refresh });
})();
